import { StorageService } from './storage.js';

// Una sola conexión WebSocket para toda la app (dashboards + campana). Antes
// cada visita al dashboard hacía `new Pusher(...)` y nunca la cerraba, así que
// se acumulaban sockets abiertos.
let client = null;
let clientPromise = null;

// pusher-js se descarga solo cuando se necesita la primera conexión.
export function getPusher() {
    if (!process.env.VUE_APP_PUSHER_KEY) return Promise.resolve(null);
    if (client) return Promise.resolve(client);
    if (!clientPromise) {
        clientPromise = import('pusher-js').then(({ default: Pusher }) => {
            const apiPath = (process.env.VUE_APP_API_PATH || '').replace(/\/+$/, '');
            client = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
                cluster: process.env.VUE_APP_PUSHER_CLUSTER,
                // Canales privados (campana): se autorizan con el Bearer de Passport.
                channelAuthorization: {
                    endpoint: `${apiPath}/broadcasting/auth`,
                    transport: 'ajax',
                    headersProvider: () => {
                        const token = StorageService.getToken();
                        return token ? { Authorization: `Bearer ${token}` } : {};
                    },
                },
            });
            return client;
        }).catch((err) => {
            clientPromise = null;
            console.error(err);
            return null;
        });
    }
    return clientPromise;
}

/**
 * Se suscribe a un canal y registra eventos; devuelve una función de limpieza
 * que quita exactamente esos handlers (para llamarla en onUnmounted). La
 * limpieza es segura aunque se llame antes de que cargue pusher-js.
 *
 * @param {string} channelName
 * @param {Record<string, Function>} events  nombre de evento -> callback
 * @param {(live: boolean) => void} [onLiveChange] estado de la conexión
 */
export function listen(channelName, events, onLiveChange) {
    if (!process.env.VUE_APP_PUSHER_KEY) return () => {};

    let cancelled = false;
    let cleanup = null;

    getPusher().then((pusher) => {
        if (cancelled || !pusher) return;

        const channel = pusher.subscribe(channelName);
        Object.entries(events).forEach(([name, cb]) => channel.bind(name, cb));

        const stateHandlers = onLiveChange
            ? {
                connected: () => onLiveChange(true),
                disconnected: () => onLiveChange(false),
                unavailable: () => onLiveChange(false),
            }
            : {};
        Object.entries(stateHandlers).forEach(([name, cb]) => pusher.connection.bind(name, cb));
        if (onLiveChange) onLiveChange(pusher.connection.state === 'connected');

        cleanup = () => {
            Object.entries(events).forEach(([name, cb]) => channel.unbind(name, cb));
            Object.entries(stateHandlers).forEach(([name, cb]) => pusher.connection.unbind(name, cb));
            pusher.unsubscribe(channelName);
        };
    });

    return () => {
        cancelled = true;
        if (cleanup) cleanup();
        cleanup = null;
    };
}
